const log = (...args) => console.log("[GeocodingService]", ...args);

const OPENCAGE_API_KEY = 'fbd10e267fe34589a5fdc56c001f1ef6';

export function mapToIso6391(bcp47Code) {
  if (bcp47Code === 'cmn-CN') {
    return 'zh';
  }
  const parts = bcp47Code.split('-');
  return parts[0] || 'en'; // Default to 'en' if no parts or invalid code
}

function normalizeNominatimLanguageCode(languageCode = '') {
  const code = String(languageCode || '').trim();
  if (!code) return '';

  // Pseudo-locale used in some environments; map to generic Arabic.
  if (code.toLowerCase() === 'ar-xa') return 'ar';
  // Mandarin should resolve via Chinese language tags.
  if (code.toLowerCase() === 'cmn-cn') return 'zh-CN';

  return code;
}

export function buildNominatimAcceptLanguage(userLanguage) {
  const normalized = normalizeNominatimLanguageCode(userLanguage);
  const parts = [];
  const pushUnique = (value) => {
    const v = String(value || '').trim();
    if (!v || parts.includes(v)) return;
    parts.push(v);
  };

  if (normalized) {
    pushUnique(normalized);
    const base = mapToIso6391(normalized);
    pushUnique(base);
  }

  pushUnique('en');

  return parts.join(',');
}

export function normalizeNominatimBoundingBox(boundingbox) {
  if (!Array.isArray(boundingbox) || boundingbox.length < 4) return null;

  const south = Number(boundingbox[0]);
  const north = Number(boundingbox[1]);
  const west = Number(boundingbox[2]);
  const east = Number(boundingbox[3]);

  if (![south, north, west, east].every(Number.isFinite)) return null;

  return {
    south: Math.min(south, north),
    north: Math.max(south, north),
    west,
    east,
  };
}

function openCageBoundsToBoundingBox(bounds) {
  const southwest = bounds?.southwest;
  const northeast = bounds?.northeast;
  if (!southwest || !northeast) return null;

  const south = Number(southwest.lat);
  const north = Number(northeast.lat);
  const west = Number(southwest.lng);
  const east = Number(northeast.lng);

  if (![south, north, west, east].every(Number.isFinite)) return null;

  return [
    String(Math.min(south, north)),
    String(Math.max(south, north)),
    String(west),
    String(east),
  ];
}

export function isLatLngWithinBounds(lat, lng, bounds) {
  const box = bounds?.south != null
    ? bounds
    : normalizeNominatimBoundingBox(bounds?.boundingbox || bounds?.boundingBox);

  if (!box) return false;

  const pointLat = Number(lat);
  const pointLng = Number(lng);
  const south = Number(box.south);
  const north = Number(box.north);
  const west = Number(box.west);
  const east = Number(box.east);

  if (![pointLat, pointLng, south, north, west, east].every(Number.isFinite)) return false;
  if (pointLat < south || pointLat > north) return false;

  // Normal boxes use west <= east. The alternate branch keeps dateline-crossing
  // boxes valid if Nominatim ever returns one.
  return west <= east
    ? pointLng >= west && pointLng <= east
    : pointLng >= west || pointLng <= east;
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function mapOpenCageTypeToNominatimAddresstype(components = {}) {
  const openCageType = String(components._type || '').trim().toLowerCase();
  const directTypeMap = {
    city: 'city',
    town: 'town',
    village: 'village',
    hamlet: 'hamlet',
    county: 'county',
    state: 'state',
    country: 'country',
    suburb: 'suburb',
    neighbourhood: 'neighbourhood',
    neighborhood: 'neighbourhood',
    road: 'road',
    building: 'building',
  };

  if (directTypeMap[openCageType]) return directTypeMap[openCageType];
  if (components.city || components._normalized_city) return 'city';
  if (components.town) return 'town';
  if (components.village) return 'village';
  if (components.hamlet) return 'hamlet';
  if (components.county) return 'county';
  if (components.state || components.region || components.province) return 'state';
  if (components.country) return 'country';
  return openCageType || '';
}

function mapOpenCageResultToNominatimLike(result, requestedLat, requestedLng) {
  if (!result) return null;

  const components = result.components || {};
  const geometry = result.geometry || {};
  const boundingbox = openCageBoundsToBoundingBox(result.bounds);
  const lat = Number(geometry.lat);
  const lon = Number(geometry.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  const address = {
    road: firstText(components.road, components.pedestrian, components.footway, components.path),
    house_number: firstText(components.house_number),
    neighbourhood: firstText(components.neighbourhood, components.neighborhood, components.quarter),
    suburb: firstText(components.suburb, components.borough, components.city_district),
    city: firstText(components.city, components._normalized_city),
    town: firstText(components.town),
    village: firstText(components.village),
    hamlet: firstText(components.hamlet),
    municipality: firstText(components.municipality),
    county: firstText(components.county),
    state_district: firstText(components.state_district),
    state: firstText(components.state, components.region, components.province),
    postcode: firstText(components.postcode),
    country: firstText(components.country),
    country_code: String(components.country_code || components['ISO_3166-1_alpha-2'] || '').trim().toLowerCase(),
    display_name: firstText(result.formatted),
  };

  const addresstype = mapOpenCageTypeToNominatimAddresstype(components);
  const distanceMeters = Number(result.distance_from_q?.meters);
  const openCageId = [
    'opencage',
    Number.isFinite(Number(requestedLat)) ? Number(requestedLat).toFixed(6) : '',
    Number.isFinite(Number(requestedLng)) ? Number(requestedLng).toFixed(6) : '',
    addresstype,
    firstText(address.city, address.town, address.village, address.county, address.state, address.country),
  ].filter(Boolean).join(':');

  return {
    address,
    lat,
    lon,
    display_name: firstText(result.formatted, address.display_name),
    place_id: openCageId,
    osm_type: 'opencage',
    osm_id: openCageId,
    addresstype,
    type: addresstype || components._type || '',
    category: components._category || 'place',
    extratags: {},
    boundingbox,
    boundingBox: normalizeNominatimBoundingBox(boundingbox),
    source: 'opencage',
    confidence: result.confidence ?? null,
    distance_from_q_meters: Number.isFinite(distanceMeters) ? distanceMeters : null,
  };
}

export async function fetchGeocodingOpenCage({ lat, lng, userLanguage }) {
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    log('[OpenCage] Skipping fallback: invalid coordinates', { lat, lng });
    return null;
  }

  const language = normalizeNominatimLanguageCode(userLanguage) || 'en';
  const params = new URLSearchParams({
    q: `${lat},${lng}`,
    key: OPENCAGE_API_KEY,
    language,
    no_annotations: '1',
    address_only: '1',
  });
  const url = `https://api.opencagedata.com/geocode/v1/json?${params.toString()}`;

  log('[OpenCage] Fetching fallback reverse geocoding data', { lat, lng, language });

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`OpenCage request failed: ${response.status}`);
    }

    const data = await response.json();
    const statusCode = Number(data?.status?.code);
    const result = Array.isArray(data?.results) ? data.results[0] : null;

    if (statusCode && statusCode !== 200) {
      log('[OpenCage] Fallback returned non-OK status', data?.status);
      return null;
    }

    if (!result) {
      log('[OpenCage] Fallback returned no results', {
        status: data?.status,
        totalResults: data?.total_results,
      });
      return null;
    }

    const mapped = mapOpenCageResultToNominatimLike(result, lat, lng);
    if (!mapped) {
      log('[OpenCage] Fallback result could not be mapped', {
        formatted: result?.formatted,
        geometry: result?.geometry,
      });
      return null;
    }

    log('[OpenCage] Fallback mapped to Nominatim-like geocoding data', {
      displayName: mapped.display_name,
      addresstype: mapped.addresstype,
      address: mapped.address,
      confidence: mapped.confidence,
      distanceFromQueryMeters: mapped.distance_from_q_meters,
      hasBoundingBox: !!mapped.boundingBox,
    });

    return mapped;
  } catch (error) {
    log('[OpenCage] Fallback error:', error.message);
    console.error('[OpenCage] Full fallback error:', error);
    return null;
  }
}

function buildTopicBounds({
  boundingbox,
  placeName,
  candidateName,
  normalizedAdminLevel,
  topicZoom,
  centerLat,
  centerLon,
}) {
  const box = normalizeNominatimBoundingBox(boundingbox);
  if (!box) return null;

  const normalizedCenterLat = Number(centerLat);
  const normalizedCenterLon = Number(centerLon);

  return {
    ...box,
    ...(Number.isFinite(normalizedCenterLat) && Number.isFinite(normalizedCenterLon)
      ? {
        centerLat: normalizedCenterLat,
        centerLon: normalizedCenterLon,
      }
      : {}),
    placeName: candidateName || placeName || '',
    requestedPlaceName: placeName || '',
    topicAdminLevel: normalizedAdminLevel || '',
    nominatimZoom: topicZoom,
    source: 'nominatim-topic-reverse',
    fetchedAt: Date.now(),
  };
}

export async function fetchGeocodingNominatim({ lat, lng, zoom = 14, userLanguage }) {
  // Skip geocoding for default position (center of Europe)
  const DEFAULT_LAT = 51.1657;
  const DEFAULT_LNG = 10.4515;
  if (Math.abs(lat - DEFAULT_LAT) < 0.001 && Math.abs(lng - DEFAULT_LNG) < 0.001) {
    log('Skipping geocoding for default position');
    return null;
  }

  // Ensure zoom level is at least 13 for Nominatim
  const nominatimZoom = Math.max(13, zoom);
  
  const acceptLanguage = buildNominatimAcceptLanguage(userLanguage);
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lng),
    format: 'json',
    zoom: String(nominatimZoom),
    addressdetails: '1',
    'accept-language': acceptLanguage,
  });
  const url = `https://nominatim.openstreetmap.org/reverse?${params.toString()}`;
  
  log(`[Geocoding] Fetching Nominatim data for lat: ${lat}, lng: ${lng}, zoom: ${nominatimZoom} (requested: ${zoom}) with accept-language: ${acceptLanguage}...`);

  try {
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Curio-Sightseer-App'
      }
    });

    if (!response.ok) {
      throw new Error(`Nominatim request failed: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data || data.error) {
      log('[Geocoding] No geocoding results found or error:', data?.error);
      return fetchGeocodingOpenCage({ lat, lng, userLanguage });
    }

    log('[Geocoding] Nominatim response:', data);
    log('[Geocoding] Address components:', data.address);

    // Return Nominatim response directly - it already has the expected structure
    return {
      address: data.address || {},
      lat: parseFloat(data.lat),
      lon: parseFloat(data.lon),
      display_name: data.display_name,
      place_id: data.place_id,
      osm_type: data.osm_type,
      osm_id: data.osm_id,
      addresstype: data.addresstype,
      type: data.type,
      category: data.category,
      extratags: data.extratags || {},
      boundingbox: data.boundingbox || null,
      boundingBox: normalizeNominatimBoundingBox(data.boundingbox),
    };
  } catch (error) {
    log('[Geocoding] Error:', error.message);
    console.error('[Geocoding] Full error:', error);
    return fetchGeocodingOpenCage({ lat, lng, userLanguage });
  }
}

// Keep the old function name as an alias for backward compatibility during migration
export const fetchGeocodingGoogle = fetchGeocodingNominatim;

const wikidataPlaceCache = new Map();

function normalizePlaceName(value = '') {
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getEntityCoordinates(entity) {
  const coordClaims = entity?.claims?.P625;
  const coordValue = coordClaims?.[0]?.mainsnak?.datavalue?.value;
  if (!coordValue || typeof coordValue.latitude !== 'number' || typeof coordValue.longitude !== 'number') {
    return null;
  }
  return { lat: coordValue.latitude, lon: coordValue.longitude };
}

function scoreWikidataCandidate({
  candidate,
  entity,
  targetPlaceName,
  targetCountryName,
  targetLat,
  targetLon,
}) {
  const label = candidate?.label || entity?.labels?.en?.value || '';
  const description = candidate?.description || entity?.descriptions?.en?.value || '';

  const normalizedTarget = normalizePlaceName(targetPlaceName);
  const normalizedLabel = normalizePlaceName(label);
  const normalizedDescription = normalizePlaceName(description);
  const normalizedCountry = normalizePlaceName(targetCountryName || '');

  let score = 0;

  if (normalizedLabel === normalizedTarget) {
    score += 50;
  } else if (normalizedLabel.startsWith(normalizedTarget) || normalizedTarget.startsWith(normalizedLabel)) {
    score += 30;
  } else if (normalizedLabel.includes(normalizedTarget)) {
    score += 20;
  }

  if (normalizedCountry && normalizedDescription.includes(normalizedCountry)) {
    score += 20;
  }

  if (/(city|town|village|municipality|capital|district|borough)/.test(normalizedDescription)) {
    score += 10;
  }

  const coords = getEntityCoordinates(entity);
  let distanceKm = null;
  if (coords && Number.isFinite(targetLat) && Number.isFinite(targetLon)) {
    distanceKm = haversineKm(targetLat, targetLon, coords.lat, coords.lon);
    // Make distance a strong signal: up to +70 when very close,
    // taper down to 0 around ~700km.
    score += Math.max(0, 70 - Math.min(70, distanceKm / 10));

    // Strong penalties for far-away matches.
    if (distanceKm > 1000) score -= 60;
    else if (distanceKm > 500) score -= 30;
  }

  if (/(disambiguation|wikimedia|surname|given name)/.test(normalizedDescription)) {
    score -= 30;
  }

  return { score, label, description, distanceKm };
}

function normalizeAdminLevel(value = '') {
  const v = normalizePlaceName(value);
  if (!v) return '';

  const aliases = {
    locality: 'city',
    postal_town: 'town',
    municipality: 'municipality',
    state_district: 'state',
    province: 'state',
    region: 'state',
    suburb: 'suburb',
    neighbourhood: 'neighbourhood',
    neighborhood: 'neighbourhood',
  };

  return aliases[v] || v;
}

function getNominatimZoomForAdminLevel(topicAdminLevel = '') {
  const adminLevel = normalizeAdminLevel(topicAdminLevel);

  const zoomByAdminLevel = {
    country: 3,
    state: 5,
    county: 8,
    city: 10,
    town: 10,
    municipality: 10,
    village: 12,
    hamlet: 14,
    suburb: 14,
    neighbourhood: 16,
  };

  return zoomByAdminLevel[adminLevel] || 10;
}

function getEntityInstanceOfQids(entity) {
  const claims = entity?.claims?.P31;
  if (!Array.isArray(claims)) return [];

  return claims
    .map((claim) => claim?.mainsnak?.datavalue?.value?.id)
    .filter(Boolean);
}

function matchesTopicAdminLevel(entity, description = '', topicAdminLevel = '') {
  const normalizedAdminLevel = normalizeAdminLevel(topicAdminLevel);
  if (!normalizedAdminLevel) {
    return { matches: false, method: 'no-topic-admin-level', normalizedAdminLevel: '' };
  }

  const instanceQids = getEntityInstanceOfQids(entity);
  const normalizedDescription = normalizePlaceName(description);

  const adminMatchers = {
    city: {
      qids: ['Q515', 'Q1093829'],
      keywords: [' city', 'capital'],
    },
    town: {
      qids: ['Q3957'],
      keywords: ['town'],
    },
    village: {
      qids: ['Q532'],
      keywords: ['village'],
    },
    hamlet: {
      qids: ['Q5084'],
      keywords: ['hamlet'],
    },
    county: {
      qids: ['Q47168'],
      keywords: ['county'],
    },
    state: {
      qids: ['Q35657', 'Q7275', 'Q11828004'],
      keywords: ['state', 'province', 'region'],
    },
    municipality: {
      qids: ['Q15284'],
      keywords: ['municipality'],
    },
    suburb: {
      qids: ['Q123705'],
      keywords: ['suburb'],
    },
    neighbourhood: {
      qids: ['Q123705', 'Q253019'],
      keywords: ['neighbourhood', 'neighborhood', 'district', 'quarter'],
    },
    country: {
      qids: ['Q6256'],
      keywords: ['country', 'sovereign state'],
    },
  };

  const matcher = adminMatchers[normalizedAdminLevel] || null;
  if (!matcher) {
    return {
      matches: false,
      method: 'unknown-admin-level',
      normalizedAdminLevel,
      instanceQids,
      normalizedDescription,
    };
  }

  const qidMatch = instanceQids.some((qid) => matcher.qids.includes(qid));
  if (qidMatch) {
    return {
      matches: true,
      method: 'p31',
      normalizedAdminLevel,
      instanceQids,
      normalizedDescription,
    };
  }

  const keywordMatch = matcher.keywords.some((keyword) => normalizedDescription.includes(keyword.trim()));
  if (keywordMatch) {
    return {
      matches: true,
      method: 'description-keyword',
      normalizedAdminLevel,
      instanceQids,
      normalizedDescription,
    };
  }

  return {
    matches: false,
    method: 'no-match',
    normalizedAdminLevel,
    instanceQids,
    normalizedDescription,
  };
}

export async function fetchTopicWikidataFromNominatim({
  lat,
  lng,
  placeName,
  topicAdminLevel,
  userLanguage,
}) {
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    log('[wikitag][Geocoding] fetchTopicWikidataFromNominatim skipped: invalid coordinates', { lat, lng, placeName, topicAdminLevel });
    return null;
  }

  try {
    const normalizedAdminLevel = normalizeAdminLevel(topicAdminLevel || '');
    const topicZoom = getNominatimZoomForAdminLevel(normalizedAdminLevel);
    const acceptLanguage = buildNominatimAcceptLanguage(userLanguage);
    const params = new URLSearchParams({
      lat: String(lat),
      lon: String(lng),
      format: 'jsonv2',
      zoom: String(topicZoom),
      addressdetails: '1',
      extratags: '1',
      'accept-language': acceptLanguage,
    });
    const url = `https://nominatim.openstreetmap.org/reverse?${params.toString()}`;

    log('[wikitag][Geocoding] Topic Wikidata resolution via Nominatim started', {
      strategy: 'reverse+extratags',
      placeName,
      requestedAdminLevel: topicAdminLevel || '',
      normalizedAdminLevel,
      topicZoom,
      lat,
      lng,
      acceptLanguage,
    });

    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Curio-Sightseer-App'
      }
    });

    if (!response.ok) {
      throw new Error(`Topic Nominatim request failed: ${response.status}`);
    }

    const data = await response.json();
    const qid = data?.extratags?.wikidata || null;

    // Optional safety check: when a topic place name is provided,
    // only accept clearly matching reverse results.
    const candidateName =
      data?.name ||
      data?.address?.[normalizedAdminLevel] ||
      data?.address?.city ||
      data?.address?.town ||
      data?.address?.village ||
      data?.address?.county ||
      data?.address?.state ||
      data?.address?.country ||
      '';
    const topicBounds = buildTopicBounds({
      boundingbox: data?.boundingbox,
      placeName,
      candidateName,
      normalizedAdminLevel,
      topicZoom,
      centerLat: data?.lat,
      centerLon: data?.lon,
    });

    let looksMatching = true;

    if (placeName) {
      const nTopic = normalizePlaceName(placeName);
      const nCandidate = normalizePlaceName(candidateName);
      looksMatching = nTopic && nCandidate && (
        nTopic === nCandidate ||
        nTopic.includes(nCandidate) ||
        nCandidate.includes(nTopic)
      );

      log('[wikitag][Geocoding] Topic Nominatim candidate comparison', {
        requestedPlace: placeName,
        candidateName,
        normalizedRequestedPlace: nTopic,
        normalizedCandidateName: nCandidate,
        looksMatching,
        topicAdminLevel: normalizedAdminLevel,
        topicZoom,
        qid,
        hasTopicBounds: !!topicBounds,
      });

      if (!looksMatching) {
        log('[wikitag][Geocoding] Topic Nominatim wikidata name mismatch, trying fallback place-name resolution:', {
          requestedPlace: placeName,
          candidateName,
          topicAdminLevel: normalizedAdminLevel,
          topicZoom,
          qid,
        });

        const fallbackQid = await resolveDisplayedPlaceWikidata({
          placeName,
          countryName: data?.address?.country || '',
          countryCode: data?.address?.country_code || '',
          lat: Number(lat),
          lon: Number(lng),
          topicAdminLevel: normalizedAdminLevel,
        });

        if (fallbackQid) {
          log('[wikitag][Geocoding] Topic mismatch fallback selected Wikidata candidate', {
            requestedPlace: placeName,
            candidateName,
            nominatimQid: qid,
            fallbackQid,
            topicAdminLevel: normalizedAdminLevel,
          });
          return {
            qid: fallbackQid,
            topicBounds: null,
            candidateName,
            topicAdminLevel: normalizedAdminLevel,
            topicZoom,
            source: 'wikidata-place-name-fallback',
          };
        }

        log('[wikitag][Geocoding] Topic mismatch fallback produced no candidate; rejecting mismatched Nominatim qid', {
          requestedPlace: placeName,
          candidateName,
          nominatimQid: qid,
          topicAdminLevel: normalizedAdminLevel,
          topicZoom,
        });
        return {
          qid: null,
          topicBounds: null,
          candidateName,
          topicAdminLevel: normalizedAdminLevel,
          topicZoom,
          source: 'nominatim-topic-reverse-mismatch',
        };
      }
    }

    if (!qid) {
      log('[wikitag][Geocoding] Topic Wikidata resolution via Nominatim returned no wikidata extratag', {
        placeName,
        candidateName,
        requestedAdminLevel: topicAdminLevel || '',
        normalizedAdminLevel,
        topicZoom,
        availableExtratagKeys: Object.keys(data?.extratags || {}),
        hasTopicBounds: !!topicBounds,
      });
      return {
        qid: null,
        topicBounds,
        candidateName,
        topicAdminLevel: normalizedAdminLevel,
        topicZoom,
        source: 'nominatim-topic-reverse',
      };
    }

    log('[wikitag][Geocoding] Topic Wikidata resolution via Nominatim succeeded', {
      placeName,
      candidateName,
      topicAdminLevel: normalizedAdminLevel,
      topicZoom,
      qid,
      hasTopicBounds: !!topicBounds,
    });

    return {
      qid,
      topicBounds,
      candidateName,
      topicAdminLevel: normalizedAdminLevel,
      topicZoom,
      source: 'nominatim-topic-reverse',
    };
  } catch (error) {
    log('[wikitag][Geocoding] fetchTopicWikidataFromNominatim failed:', error.message);
    return null;
  }
}

export async function resolveDisplayedPlaceWikidata({
  placeName,
  countryName,
  countryCode,
  lat,
  lon,
  topicAdminLevel,
}) {
  const normalizedPlace = normalizePlaceName(placeName);
  if (!normalizedPlace) return null;

  const normalizedTopicAdminLevel = normalizeAdminLevel(topicAdminLevel || '');
  const cacheKey = `${normalizedPlace}|${String(countryCode || '').toLowerCase()}|${normalizedTopicAdminLevel}`;
  if (wikidataPlaceCache.has(cacheKey)) {
    return wikidataPlaceCache.get(cacheKey);
  }

  const targetLat = Number(lat);
  const targetLon = Number(lon);
  if (!Number.isFinite(targetLat) || !Number.isFinite(targetLon)) {
    log('[wikitag][Geocoding] resolveDisplayedPlaceWikidata skipped: invalid target coordinates', {
      placeName,
      lat,
      lon,
      topicAdminLevel: normalizedTopicAdminLevel,
    });
    wikidataPlaceCache.set(cacheKey, null);
    return null;
  }

  try {
    const MAX_DISTANCE_KM = 400;

    const searchUrl = `https://www.wikidata.org/w/api.php?action=wbsearchentities&format=json&language=en&type=item&limit=10&origin=*&search=${encodeURIComponent(placeName)}`;
    const searchResponse = await fetch(searchUrl);
    if (!searchResponse.ok) {
      throw new Error(`Wikidata search failed: ${searchResponse.status}`);
    }

    const searchData = await searchResponse.json();
    const candidates = Array.isArray(searchData?.search) ? searchData.search : [];
    if (candidates.length === 0) {
      wikidataPlaceCache.set(cacheKey, null);
      log('[wikitag][Geocoding] Wikidata admin-level selection: no search candidates', {
        placeName,
        countryName,
        topicAdminLevel: normalizedTopicAdminLevel,
      });
      return null;
    }

    const ids = candidates.map(c => c.id).filter(Boolean).slice(0, 10);
    if (ids.length === 0) {
      wikidataPlaceCache.set(cacheKey, null);
      return null;
    }

    const entitiesUrl = `https://www.wikidata.org/w/api.php?action=wbgetentities&format=json&languages=en&props=claims|labels|descriptions|aliases&origin=*&ids=${encodeURIComponent(ids.join('|'))}`;
    const entitiesResponse = await fetch(entitiesUrl);
    if (!entitiesResponse.ok) {
      throw new Error(`Wikidata entities fetch failed: ${entitiesResponse.status}`);
    }

    const entitiesData = await entitiesResponse.json();
    const entities = entitiesData?.entities || {};

    const searchOrderedCandidates = candidates.map((candidate, index) => {
      const entity = entities[candidate.id] || null;
      const coords = getEntityCoordinates(entity);
      const distanceKm = coords
        ? haversineKm(targetLat, targetLon, coords.lat, coords.lon)
        : Number.POSITIVE_INFINITY;

      const label = candidate?.label || entity?.labels?.en?.value || '';
      const aliases = (entity?.aliases?.en || []).map((a) => a?.value).filter(Boolean);
      const normalizedLabel = normalizePlaceName(label);
      const normalizedAliases = aliases.map((value) => normalizePlaceName(value));
      const description = candidate?.description || entity?.descriptions?.en?.value || '';
      const adminLevelMatch = matchesTopicAdminLevel(entity, description, normalizedTopicAdminLevel);

      return {
        index,
        id: candidate.id,
        label,
        description,
        aliases,
        distanceKm,
        hasCoords: Number.isFinite(distanceKm),
        directLabelMatch: normalizedLabel === normalizedPlace,
        aliasMatch: normalizedAliases.includes(normalizedPlace),
        adminLevelMatch,
      };
    });

    const decisionTrace = [];

    let selectedCandidate = null;
    let reason = 'no-candidate-within-threshold';

    if (normalizedTopicAdminLevel) {
      for (const item of searchOrderedCandidates) {
        const decision = {
          step: 'admin-level-check',
          candidateIndex: item.index,
          candidateId: item.id,
          label: item.label,
          description: item.description,
          topicAdminLevel: normalizedTopicAdminLevel,
          adminLevelMatch: item.adminLevelMatch,
          distanceKm: Number.isFinite(item.distanceKm) ? Math.round(item.distanceKm * 10) / 10 : null,
        };

        decisionTrace.push(decision);
        log('[wikitag][Geocoding] Wikidata admin-level candidate decision:', decision);

        if (item.adminLevelMatch?.matches) {
          selectedCandidate = item;
          reason = `first-admin-level-match-in-search-order:${item.adminLevelMatch.method}`;
          break;
        }
      }
    }

    const distanceRanked = [...searchOrderedCandidates].sort((a, b) => a.distanceKm - b.distanceKm);
    const pickClosestWithinThreshold = (items) =>
      items.find((item) => item.hasCoords && item.distanceKm <= MAX_DISTANCE_KM) || null;

    const directLabelMatches = distanceRanked.filter((item) => item.directLabelMatch);
    const aliasMatches = distanceRanked.filter((item) => item.aliasMatch);

    // If no admin-level selection happened, use the previous distance-based strategy.
    if (!selectedCandidate) {
      selectedCandidate = pickClosestWithinThreshold(directLabelMatches);
      if (selectedCandidate) {
        reason = 'closest-direct-label-match-within-threshold';
      }
    }

    if (!selectedCandidate) {
      selectedCandidate = pickClosestWithinThreshold(aliasMatches);
      if (selectedCandidate) {
        reason = 'closest-alias-match-within-threshold';
      }
    }

    if (!selectedCandidate) {
      selectedCandidate = pickClosestWithinThreshold(distanceRanked);
      if (selectedCandidate) {
        reason = 'closest-distance-within-threshold';
      }
    }

    const chosen = selectedCandidate ? selectedCandidate.id : null;
    wikidataPlaceCache.set(cacheKey, chosen);

    log('[wikitag][Geocoding] Wikidata admin-level selection summary:', {
      placeName,
      countryName,
      topicAdminLevel: normalizedTopicAdminLevel,
      targetLat,
      targetLon,
      maxDistanceKm: MAX_DISTANCE_KM,
      chosen,
      reason,
      selectedCandidate,
      topSearchCandidates: searchOrderedCandidates.slice(0, 5).map((item) => ({
        index: item.index,
        id: item.id,
        label: item.label,
        description: item.description,
        distanceKm: Number.isFinite(item.distanceKm) ? Math.round(item.distanceKm * 10) / 10 : null,
        adminLevelMatch: item.adminLevelMatch,
      })),
      directLabelMatches: directLabelMatches.slice(0, 5),
      aliasMatches: aliasMatches.slice(0, 5),
      decisionTrace,
    });

    return chosen;
  } catch (error) {
    log('[wikitag][Geocoding] resolveDisplayedPlaceWikidata failed:', error.message);
    return null;
  }
}

export function getWikiLanguageFromNominatim(nominatimData) {
  if (!nominatimData) return 'en'; // Default to English

  // Extract language from address.country_code provided by Nominatim
  const countryCode = String(nominatimData?.address?.country_code || '').toLowerCase();

  // Map country codes to Wikipedia language codes (primary language per country)
  const countryToLang = {
    // English-speaking countries
    'us': 'en', 'gb': 'en', 'au': 'en', 'ca': 'en', 'nz': 'en', 'ie': 'en',
    'za': 'en', 'jm': 'en', 'bz': 'en', 'tt': 'en', 'bs': 'en', 'bb': 'en',

    // European languages
    'de': 'de', 'at': 'de', 'ch': 'de', // German
    'fr': 'fr', 'mc': 'fr', // French
    'es': 'es', 'mx': 'es', 'ar': 'es', 'co': 'es', 'pe': 'es',
    've': 'es', 'cl': 'es', 'ec': 'es', 'gt': 'es', 'cu': 'es',
    'bo': 'es', 'do': 'es', 'hn': 'es', 'py': 'es', 'sv': 'es',
    'ni': 'es', 'cr': 'es', 'pa': 'es', 'uy': 'es', // Spanish
    'it': 'it', 'sm': 'it', 'va': 'it', // Italian
    'pt': 'pt', 'br': 'pt', 'ao': 'pt', 'mz': 'pt', // Portuguese
    'nl': 'nl', 'be': 'nl', 'sr': 'nl', // Dutch
    'pl': 'pl', // Polish
    'se': 'sv', 'fi': 'fi', // Swedish, Finnish
    'no': 'no', 'dk': 'da', // Norwegian, Danish
    'cz': 'cs', 'sk': 'sk', // Czech, Slovak
    'hu': 'hu', 'ro': 'ro', 'bg': 'bg', 'gr': 'el', // Hungarian, Romanian, Bulgarian, Greek

    // Asian languages
    'jp': 'ja', // Japanese
    'cn': 'zh', 'tw': 'zh', 'hk': 'zh', // Chinese
    'kr': 'ko', // Korean
    'th': 'th', // Thai
    'vn': 'vi', // Vietnamese
    'id': 'id', // Indonesian
    'my': 'ms', // Malaysian
    'in': 'hi', // Hindi (India)
    'pk': 'ur', // Urdu (Pakistan)
    'bd': 'bn', // Bengali (Bangladesh)

    // Middle Eastern languages
    'il': 'he', // Hebrew
    'sa': 'ar', 'ae': 'ar', 'qa': 'ar', 'kw': 'ar', 'bh': 'ar',
    'om': 'ar', 'jo': 'ar', 'lb': 'ar', 'sy': 'ar', 'iq': 'ar',
    'eg': 'ar', 'ly': 'ar', 'dz': 'ar', 'ma': 'ar', 'tn': 'ar', // Arabic
    'ir': 'fa', // Persian
    'tr': 'tr', // Turkish

    // Eastern European languages
    'ru': 'ru', 'by': 'be', 'ua': 'uk', // Russian, Belarusian, Ukrainian

    // Other languages
    'is': 'is', // Icelandic
    'ee': 'et', // Estonian
    'lv': 'lv', // Latvian
    'lt': 'lt', // Lithuanian
    'hr': 'hr', // Croatian
    'rs': 'sr', // Serbian
    'si': 'sl', // Slovenian
    'mk': 'mk', // Macedonian
    'al': 'sq', // Albanian
    'ge': 'ka', // Georgian
    'am': 'hy', // Armenian
    'az': 'az', // Azerbaijani
    'mn': 'mn', // Mongolian
    'kz': 'kk', // Kazakh
    'uz': 'uz', // Uzbek
    'tm': 'tk', // Turkmen
    'kg': 'ky', // Kyrgyz
    'tj': 'tg', // Tajik
  };

  // Log the country code for debugging
  console.log(`Country code from Nominatim: ${countryCode}`);

  // Resolve the candidate Wikipedia language (falls back to 'en' if no mapping found)
  const candidateLang = countryToLang[countryCode] || 'en';

  /*
   * Behaviour note:
   * --------------------------------------------------------------
   * We deliberately restrict the Wikipedia edition to languages that
   * use the Latin alphabet.  If the mapped language uses a non-Latin
   * script (e.g. Chinese, Japanese, Arabic, Cyrillic, etc.) we fall
   * back to English.  This keeps article titles readable in Western
   * locales and avoids font-support issues.
   *
   * TODO: Consider preferring a Wikipedia edition that matches
   *       the user interface language (userLanguage) when that is
   *       available and different from English.
   */
  const nonLatinLangs = new Set([
    'zh', // Chinese
    'ja', // Japanese
    'ko', // Korean
    'ar', // Arabic
    'he', // Hebrew
    'fa', // Persian
    'ru', // Russian
    'uk', // Ukrainian
    'bg', // Bulgarian
    'el', // Greek
    'hy', // Armenian
    'ka', // Georgian
    'th', // Thai
    'bn', // Bengali
    'ur', // Urdu
    'hi', // Hindi
    'km', // Khmer
    'lo', // Lao
    'si', // Sinhala
  ]);

  return nonLatinLangs.has(candidateLang) ? 'en' : candidateLang;
}
